// The formatting primitives every composed sentence in the product uses.
//
// They live here, shared, for one reason: a sentence explaining a condition is composed ONCE
// and rendered unedited. If the server writes "2 proposals" and a client helper writes
// "2 proposal(s)" for the same count, the two sides already disagree about the same fact.
//
// No `pg`, no React, no src/server imports.

/** ISO 'YYYY-MM-DD' → 'dd.mm.yyyy'. Slice, never Date — a Date would apply a timezone. */
export const fmtDate = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/** '1 proposal' / '2 proposals'; pass `many` where English refuses the -s ('touch' → 'touches'). */
export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * Swiss thousands: 12500 → 12’500. The separator is U+2019 RIGHT SINGLE QUOTATION MARK, which
 * is what Swiss typography actually uses — not an apostrophe, not a space.
 */
export const fmtNumber = (n: number | string | null | undefined): string => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  const [whole, frac] = Math.abs(num).toFixed(Number.isInteger(num) ? 0 : 2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '’');
  return `${num < 0 ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
};

/** 'CHF 12’500'. Money is a quantity: it renders in .voice-figure, never in mono. */
export const fmtMoney = (amount: number | string | null | undefined, currency = 'CHF'): string =>
  amount === null || amount === undefined || amount === '' ? '—' : `${currency} ${fmtNumber(amount)}`;

/** Whole days between two ISO dates, ignoring clocks entirely. */
export const daysBetween = (fromIso: string, toIso: string): number =>
  Math.round((Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) / 86_400_000);

/** Initials for an avatar fallback: 'Anne-Sophie Girard' → 'AG'. Never more than two. */
export const initials = (name: string | null | undefined): string => {
  const parts = (name ?? '').trim().split(/[\s\-]+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
